import { useState } from "react";
import { creditBreakdown } from "../../utils/workspaceRequirements";
import "../../styles/landlord-profile-preview.css";

// The frontend's own line icons (uno-frontend/src/components/design/Icon).
const PATHS = {
  lock: "M7 11V8a5 5 0 0110 0v3 M6 11h12v9H6z",
  users:
    "M9 11a3.5 3.5 0 100-7 3.5 3.5 0 000 7z M2.5 20a6.5 6.5 0 0113 0 M16 4.5a3.5 3.5 0 010 6.5 M18 14a6 6 0 013.5 6",
  chevronDown: "M6 9l6 6 6-6",
  chevronRight: "M9 6l6 6-6 6",
  link: "M10 14a4 4 0 005.7 0l3-3a4 4 0 00-5.7-5.7l-1 1 M14 10a4 4 0 00-5.7 0l-3 3a4 4 0 005.7 5.7l1-1",
  building:
    "M5 21V4h10v17 M15 9h4v12 M3 21h18 M8 8h1 M11 8h1 M8 12h1 M11 12h1 M8 16h1 M11 16h1",
  download: "M12 4v12 M7 11l5 5 5-5 M5 20h14",
  briefcase: "M4 8h16v11H4z M9 8V5h6v3 M4 13h16",
  idCard:
    "M3 6h18v12H3z M8.5 12.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3z M5.5 16a3 3 0 016 0 M14 10h4 M14 14h3",
  shield: "M12 3l8 3v6c0 4.5-3.2 7.5-8 9-4.8-1.5-8-4.5-8-9V6z",
  shieldCheck:
    "M12 3l8 3v6c0 4.5-3.2 7.5-8 9-4.8-1.5-8-4.5-8-9V6z M8.5 12l2.5 2.5 4.5-5",
  fileText: "M6 3h9l4 4v14H6z M14 3v5h5 M9 13h7 M9 17h5",
  wallet: "M4 7h15v12H4z M4 7l12-3v3 M15 13h4",
  user: "M12 12a4 4 0 100-8 4 4 0 000 8z M4 21a8 8 0 0116 0",
  chevronLeft: "M15 6l-6 6 6 6",
  swap: "M7 4L3 8l4 4 M3 8h14 M17 20l4-4-4-4 M21 16H7",
  info: "M12 21a9 9 0 100-18 9 9 0 000 18z M12 11v5 M12 8v.01",
};

const Icon = ({ name, size = 20, className = "", style, label }) => (
  <svg
    className={`ds-icon ${className}`.trim()}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    style={style}
    role={label ? "img" : undefined}
    aria-label={label}
    aria-hidden={label ? undefined : true}
  >
    <path d={PATHS[name]} />
  </svg>
);

// A copy of the landlord's tenant profile (uno-frontend, TenantDetails) with
// made-up data. A feature the workspace has not switched on is shown the same
// way the landlord sees it: a "Not enabled" badge, a lock icon, or a faded
// card. Nothing here is saved or sent.
//
// Which switch locks what (same rules as src/hooks/useWorkspaceRequirements
// in the frontend):
//   KYC / AML           header "KYC & AML checks"
//   Defaulter check     header "Defaulter check"
//   Insurability check  header "Insurable"
//   Guarantor           header "Guarantors", guarantor options of the effort rate
//   Financial assessment  changing the income, effort rate table
//   Open Banking        the Financial Skor card
//   Basic documents     the whole Documents card
//   Document forensics  the verdict filters and the status of each document
//   Applicant questions  the Applicant questions card

const LOCKED = "Not enabled for this workspace";

const LockedBadge = () => (
  <span className="pv-locked-badge" title={LOCKED}>
    <Icon name="lock" size={14} aria-hidden="true" />
    Not enabled
  </span>
);

const LockedIcon = () => (
  <span className="pv-locked-icon" title={LOCKED}>
    <Icon name="lock" size={16} label={LOCKED} />
  </span>
);

// Faded, unclickable card with the message on top.
const LockedFeature = ({ locked, children }) => {
  if (!locked) return children;
  return (
    <div className="pv-locked-feature">
      <div className="pv-locked-feature__content" aria-hidden="true">
        {children}
      </div>
      <div className="pv-locked-feature__message">
        <Icon name="lock" size={20} aria-hidden="true" />
        <span>{LOCKED}</span>
      </div>
    </div>
  );
};

const Badge = ({ tone = "lime", children }) => (
  <span className={`ds-badge ds-badge--${tone}`}>{children}</span>
);

const Card = ({ title, actions, className = "", children }) => (
  <div className={`ds-card ${className}`.trim()}>
    {(title || actions) && (
      <div className="ds-card__head">
        <div>{title && <h2 className="ds-card__title">{title}</h2>}</div>
        {actions && <div className="ds-card__actions">{actions}</div>}
      </div>
    )}
    {children}
  </div>
);

const Stat = ({ icon, label, value, chevron = false }) => (
  <div className="ds-stat">
    <span className="ds-stat__label">
      {icon}
      <span className="ds-stat__label-text">{label}</span>
    </span>
    <span className="ds-stat__value">
      {value}
      {chevron && (
        <Icon name="chevronRight" size={16} className="ds-stat__chevron" />
      )}
    </span>
  </div>
);

const Field = ({ label, value, verified = false }) => (
  <div className="tic-field">
    <div className="pv-field-label">
      <p className="tic-field__label">{label}</p>
      {verified && <span className="tic-check">✓</span>}
    </div>
    <p className="tic-field__value">{value}</p>
  </div>
);

const SAMPLE_ANSWER = {
  number: "3",
  yes_no: "Yes",
  date: "01/11/2026",
  link: "web.com/…",
  file: "document.pdf",
};

const EFFORT = 35; // sample effort rate, in %
const EFFORT_MIN = 10;
const EFFORT_MAX = 50;

function EffortRateCard({ financial, guarantor, financialAssessment }) {
  const [mode, setMode] = useState("tenant");
  const [calculator, setCalculator] = useState(false);
  const options = [
    ["tenant", "Tenant"],
    ["guarantor", "Guarantor"],
    ["both", "Combined"],
  ];
  const active = !guarantor && mode !== "tenant" ? "tenant" : mode;
  const zone = EFFORT <= 35 ? "ok" : EFFORT <= 45 ? "stretch" : "risk";
  const position = ((EFFORT - EFFORT_MIN) / (EFFORT_MAX - EFFORT_MIN)) * 100;
  const rows = [
    ["Tenant", "€2,155", "35%", "tenant"],
    ["Guarantor", "€2,100", "55%", "guarantor"],
    ["Combined", "€4,255", "18%", "both"],
  ];
  const income = { tenant: "€2,155", guarantor: "€2,100", both: "€4,255" };
  const people = active === "both" ? 2 : 1;

  return (
    <Card
      title="Effort Rate"
      className="dsk-card"
      actions={
        !calculator && (
          <div className="dsk-toggle">
            {options.map(([key, label]) => {
              const disabled = !guarantor && key !== "tenant";
              return (
                <button
                  key={key}
                  type="button"
                  disabled={disabled}
                  title={disabled ? LOCKED : undefined}
                  className={`ds-btn${active === key ? " ds-btn--primary" : ""}`}
                  onClick={() => setMode(key)}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )
      }
    >
      <p className="dsk-text">
        {calculator
          ? "Key financial metrics for your rental situation"
          : "Adjust the rent to see if it fits the incomes"}
      </p>
      {calculator ? (
        <div className="effort-rate-table">
          <div className="effort-rate-table-heading">
            <p title={financialAssessment ? undefined : LOCKED}>Category</p>
            <p>Monthly net income</p>
            <p>Effort rate</p>
          </div>
          {rows
            .filter(([, , , key]) => guarantor || key === "tenant")
            .map(([label, amount, rate, key]) => (
              <div
                key={label}
                className={`effort-rate-table-info${
                  active === key || (active === "both" && key !== "both")
                    ? ""
                    : " text_disabled"
                }`}
              >
                <p
                  className={financialAssessment ? "pv-underline" : ""}
                  title={financialAssessment ? undefined : LOCKED}
                >
                  {label}
                </p>
                <p>{financial ? amount : "—"}</p>
                <p
                  style={{
                    color: financial
                      ? rate === "55%"
                        ? "var(--danger)"
                        : rate === "35%"
                          ? "var(--orange)"
                          : "var(--success)"
                      : undefined,
                  }}
                >
                  {financial ? rate : "—"}
                </p>
              </div>
            ))}
          <div className="dsk-card__footer">
            <button
              type="button"
              className="pv-btn-secondary"
              onClick={() => setCalculator(false)}
            >
              <Icon name="chevronLeft" size={16} /> Back to previous view
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className={`dsk-meter is-${zone}`}>
            <div className="dsk-meter__rail">
              <div
                className="dsk-meter__track"
                style={{ width: `${position}%` }}
              />
              <div
                className="dsk-meter__handle"
                style={{ left: `${position}%` }}
              />
            </div>
            <div className="dsk-meter__labels">
              <span className="dsk-meter__label dsk-meter__label--ok">
                Affordable
              </span>
              <span className="dsk-meter__label dsk-meter__label--stretch">
                Moderate Effort
              </span>
              <span className="dsk-meter__label dsk-meter__label--risk">
                High Effort
              </span>
            </div>
          </div>
          <div className="pv-two pv-effort-stats">
            <div className="dsk-stat">
              <span className="dsk-stat__icon">€</span>
              <div>
                <p
                  className="dsk-label pv-effort-link"
                  title={financialAssessment ? undefined : LOCKED}
                >
                  <span className={financialAssessment ? "pv-underline" : ""}>
                    Selected Net Income
                  </span>
                  {financialAssessment ? (
                    <Icon name="chevronRight" size={14} />
                  ) : (
                    <Icon name="lock" size={14} />
                  )}
                  <Icon name="user" size={13} />
                  {people}
                </p>
                <p className="dsk-number">{financial ? income[active] : "—"}</p>
              </div>
            </div>
            <div className="dsk-stat">
              <span className="dsk-stat__icon">
                <Icon name="wallet" size={18} />
              </span>
              <div>
                <p className="dsk-label pv-effort-link">
                  Recommended Monthly Rent <Icon name="info" size={14} />
                </p>
                <p className="dsk-number">
                  {financial ? "€754" : "—"}
                  {financial && (
                    <span className="pv-effort-rate">
                      {" "}
                      ({EFFORT}% Effort Rate)
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
          <div className="dsk-card__footer">
            <button
              type="button"
              className="pv-btn-secondary"
              onClick={() => setCalculator(true)}
            >
              Effort Rate Calculator <Icon name="chevronRight" size={16} />
            </button>
          </div>
        </>
      )}
    </Card>
  );
}

function FinancialSkorCard({ openBanking, statements }) {
  const hasScore = openBanking || statements;
  const score = openBanking ? 977 : 912;
  const percent = (score / 1000) * 100;
  const card = (
    <Card title="Financial Skor" className="dsk-card">
      <div className="pv-skor">
        <div
          className="pv-skor__gauge"
          style={{
            background: hasScore
              ? `conic-gradient(#2ca143 0 ${percent * 0.78}%, #e6f0e9 ${percent * 0.78}% 78%, transparent 78%)`
              : "conic-gradient(#e6f0e9 0 78%, transparent 78%)",
          }}
        >
          <div className="pv-skor__inner dsk-score">
            <div className="dsk-score__value">{hasScore ? score : "—"}</div>
            <div className="dsk-score__max">/1000</div>
          </div>
        </div>
        <div className="pv-skor__details">
          <div>
            <p className="dsk-label">Score status:</p>
            <h2 className="dsk-status ExtremelyText">Extremely reliable</h2>
          </div>
          <div className="pv-two">
            <div>
              <p className="dsk-label">Created on:</p>
              <p className="dsk-value">01-11-2026</p>
            </div>
            <div>
              <p className="dsk-label">Valid till:</p>
              <p className="dsk-value">01-01-2027</p>
            </div>
          </div>
          <div>
            <p className="dsk-label">Account holder:</p>
            <p className="dsk-value">María Martín</p>
          </div>
        </div>
      </div>
    </Card>
  );
  // No bank data and Open Banking off: the landlord sees the bank card locked.
  return <LockedFeature locked={!hasScore}>{card}</LockedFeature>;
}

const DOC_GROUPS = [
  ["Identification", "idCard", "2 documents"],
  ["Employment", "briefcase", "2 documents"],
  ["Income and taxes", "wallet", "4 documents"],
];

const DOC_ROWS = {
  Identification: ["DNI front", "DNI back"],
  Employment: ["Employment contract", "Vida laboral"],
  "Income and taxes": ["Payslip March", "Payslip April", "Payslip May"],
  Guarantor: ["Guarantor DNI", "Guarantor payslip"],
  "Source of funds declaration": ["Declaration (signed)"],
};

const DOC_ICON = {
  idCard: "idCard",
  briefcase: "briefcase",
  wallet: "wallet",
  guarantor: "users",
  declaration: "fileText",
};

function DocumentsCard({ forensics, guarantor, affidavit }) {
  const [open, setOpen] = useState("Identification");
  const groups = [
    ...DOC_GROUPS,
    guarantor && ["Guarantor", "guarantor", "2 documents"],
    affidavit && ["Source of funds declaration", "declaration", "1 document"],
  ].filter(Boolean);
  const total = groups.reduce((sum, [name]) => sum + DOC_ROWS[name].length, 0);

  return (
    <Card className="details-docs">
      <div className="details-docs__head">
        <div className="pv-docs-title">
          <Icon name="fileText" size={20} aria-hidden="true" />
          <h2 className="ds-card__title">Documents</h2>
          <Badge tone="ink">{total}</Badge>
        </div>
        <Icon name="download" size={16} label="Download all documents" />
      </div>
      {forensics && (
        <div className="details-docs__filters">
          {["Trusted", "Safe", "Review", "High risk"].map((label) => (
            <span key={label} className="pv-chip">
              {label}
            </span>
          ))}
        </div>
      )}
      {groups.map(([name, icon, count]) => {
        const iconName = DOC_ICON[icon];
        const isOpen = open === name;
        return (
          <div key={name} className="details-docs__cat">
            <div
              className="details-docs__cat-head"
              role="button"
              tabIndex={0}
              onClick={() => setOpen(isOpen ? "" : name)}
              onKeyDown={(event) =>
                event.key === "Enter" && setOpen(isOpen ? "" : name)
              }
            >
              <span className="details-docs__cat-title">
                <Icon name={iconName} size={18} />
                {name}
              </span>
              <span className="details-docs__cat-meta">
                <span className="details-docs__cat-count">{count}</span>
                <Icon
                  name="chevronDown"
                  size={16}
                  className="details-docs__cat-chevron"
                  style={{ transform: isOpen ? "rotate(180deg)" : undefined }}
                />
              </span>
            </div>
            {isOpen && (
              <div className="details-docs__rows">
                {DOC_ROWS[name].map((doc) => (
                  <div key={doc} className="details-docs__row">
                    <span className="details-docs__name">{doc}</span>
                    {forensics ? (
                      <Badge tone="lime">Trusted</Badge>
                    ) : (
                      <LockedIcon />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </Card>
  );
}

export default function WorkspaceProfilePreview({ form }) {
  const config = form.requirements_config;
  const questions = config.applicant_questions;
  const household = form.tenant_requirements.household;
  const { total, lines } = creditBreakdown(form);

  const kyc = config.kyc.active;
  const aml = config.aml.active;
  const docs = config.basic_documents.active;
  const forensics = config.document_forensics.active;
  const defaulter = config.defaulter_check.active;
  const statements = config.financial_assessment.active;
  const bank = config.open_banking.active;
  const guarantor = config.guarantor.active;
  const insurable = form.insurance_service_status;
  const affidavit = form.affidavit_activated;

  const answers = household.active
    ? [
        questions.linkedin && ["LinkedIn", "linkedin.com/in/maria-m"],
        questions.household_members && ["Total household members", "2"],
        questions.minors && ["Number of minors", "0"],
        questions.dogs && ["Dogs", "Yes · 1"],
        ...questions.custom
          .filter((q) => q.active)
          .map((q) => [
            q.text,
            SAMPLE_ANSWER[q.type] ||
              (q.type === "multiple_choice"
                ? q.options.slice(0, 2).join(", ")
                : q.options[0] || "Answer"),
          ]),
      ].filter(Boolean)
    : [];

  return (
    <aside className="lw-rq-preview" aria-label="Profile simulator">
      <div className="lw-pv">
        <div className="pv-intro">
          <div>
            <h3>Profile simulator</h3>
            <p>
              What the landlord sees for a tenant of this workspace, with
              example data.
            </p>
          </div>
          <Badge tone="neutral">Example</Badge>
        </div>

        <div className="pv-credits">
          <div>
            <span className="pv-credits__label">Each profile will use</span>
            <b>{total}</b>
            <span className="pv-credits__label">credits</span>
          </div>
          <div className="pv-credits__lines">
            {lines.map(([label, value], index) => (
              <span key={label}>
                {label} {index === 0 ? value : `+${value}`}
              </span>
            ))}
          </div>
        </div>

        <div className="pv-page">
          {/* Header strip */}
          <div className="ds-card tdh-card">
            <div className="tdh-lead">
              <span className="pv-back" aria-hidden="true">
                <Icon name="chevronLeft" size={18} />
              </span>
              <span className="ds-avatar ds-avatar--lg">MM</span>
            </div>
            <div className="tdh-stats">
              <Stat
                icon={<Icon name="wallet" size={16} />}
                label="Selected annual income"
                value={
                  <>
                    {statements || bank || docs ? "€25,860" : "—"}
                    <span
                      className="pv-swap"
                      title={statements ? undefined : LOCKED}
                    >
                      <Icon name={statements ? "swap" : "lock"} size={16} />
                    </span>
                  </>
                }
              />
              <Stat
                icon={<Icon name="briefcase" size={16} />}
                label="Employment status"
                value="Employed"
              />
              <Stat
                icon={<Icon name="idCard" size={16} />}
                label="Identity (KYC & AML)"
                chevron={aml}
                value={
                  kyc || aml ? (
                    <Badge tone="lime">Approved</Badge>
                  ) : (
                    <LockedBadge />
                  )
                }
              />
              <Stat
                icon={<Icon name="shield" size={16} />}
                label="Default-free"
                chevron={defaulter}
                value={
                  defaulter ? <Badge tone="lime">Yes</Badge> : <LockedBadge />
                }
              />
              <Stat
                icon={<Icon name="shieldCheck" size={16} />}
                label="Insurable"
                chevron={insurable}
                value={
                  insurable ? <Badge tone="lime">Yes</Badge> : <LockedBadge />
                }
              />
              <Stat
                icon={<Icon name="users" size={16} />}
                label="Guarantors"
                chevron={guarantor}
                value={guarantor ? "1" : <LockedBadge />}
              />
              <Stat
                icon={<Icon name="link" size={16} />}
                label="Flatmates"
                chevron
                value="0"
              />
              <Stat
                icon={<Icon name="building" size={16} />}
                label="Linked Properties"
                chevron
                value="0"
              />
              <Stat
                icon={<Icon name="fileText" size={16} />}
                label="Summary"
                chevron
                value="View"
              />
            </div>
          </div>

          {/* Effort rate and Financial Skor */}
          <div className="mainEffortRateWrapper">
            <EffortRateCard
              financial={statements || bank || docs}
              guarantor={guarantor}
              financialAssessment={statements}
            />
            <FinancialSkorCard openBanking={bank} statements={statements} />
          </div>

          {/* Personal and employment information */}
          <div className="pv-two-cols">
            <Card title="Personal information" className="tic-card">
              <div className="pv-fields">
                <Field label="Name" value="María Martín" verified={kyc} />
                <Field label="Nationality" value="Spain" verified={kyc} />
                <Field label="Phone number" value="+34 612 345 612" verified />
                <Field label="Email" value="maria.martin@mail.com" verified />
                <Field
                  label="Date of birth"
                  value="14 March 1993"
                  verified={kyc}
                />
              </div>
            </Card>
            <Card title="Employment information" className="tic-card">
              <div className="pv-fields">
                <Field label="Employment status" value="Employed" />
                <Field label="Employer name" value="Grupo Norte S.L." />
                <Field label="Employee seniority" value="2 years" />
              </div>
            </Card>
          </div>

          {/* Income */}
          <Card title="Income information" className="tic-card">
            <div className="tic-income-list">
              {[
                ["Declared income", "€3,300", "monthly", "change"],
                ["Payslips", "€3,300", "3 selected", "change"],
                ["Net income from bank", "€3,250", "Open Banking", "auto"],
              ]
                .filter(([label]) => label !== "Net income from bank" || bank)
                .map(([label, amount, meta, kind]) => (
                  <div key={label} className="tic-income-row">
                    <span className="tic-income-row__info">
                      <span className="tic-income-row__label">{label}</span>
                      <span className="tic-income-row__value">
                        <b>{amount}</b>
                        <span className="tic-income-row__meta">{meta}</span>
                      </span>
                    </span>
                    {kind === "auto" ? (
                      <Badge tone="neutral">
                        <Icon name="lock" size={12} aria-hidden="true" />
                        Automatic
                      </Badge>
                    ) : statements ? (
                      <span className="pv-btn-secondary">
                        Change{" "}
                        <Icon
                          name="chevronRight"
                          size={14}
                          aria-hidden="true"
                        />
                      </span>
                    ) : (
                      <LockedBadge />
                    )}
                  </div>
                ))}
            </div>
          </Card>

          {/* Documents */}
          <LockedFeature locked={!docs}>
            <DocumentsCard
              forensics={forensics}
              guarantor={guarantor}
              affidavit={affidavit}
            />
          </LockedFeature>

          {/* Applicant questions (shown with the application) */}
          <LockedFeature locked={!household.active}>
            <Card title="Applicant questions" className="tic-card">
              <div className="pv-fields">
                {(answers.length
                  ? answers
                  : [
                      ["Total household members", "2"],
                      ["Number of minors", "0"],
                      ["Dogs", "Yes · 1"],
                    ]
                ).map(([label, value]) => (
                  <Field key={label} label={label} value={value} />
                ))}
              </div>
            </Card>
          </LockedFeature>
        </div>
      </div>
    </aside>
  );
}
